import { ArrowRight, ArrowUpRight } from "lucide-react"
import { useEffect } from "react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { OperationsMock } from "@/components/marketing/WorkMockMedia"
import webshopImage from "../../assets/marketing/projects/cajanka-webshop.webp"
import { CAJANKA_COPY } from "@/content/marketing-work"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { MarketingPageProps } from "@/types/marketing"

export default function MarketingCajanka(props: MarketingPageProps) {
  const copy = CAJANKA_COPY[props.locale]
  useEffect(() => {
    captureAnalyticsEvent("case_study_viewed", { locale: props.locale, page_id: props.page_key })
  }, [props.locale, props.page_key])
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        name: props.title,
        headline: copy.hero.heading,
        description: props.description,
        url: props.canonical_url,
        inLanguage: props.locale === "hr" ? "hr-HR" : "en",
        author: {
          "@type": "Organization",
          name: "Zmile d.o.o.",
          url: "https://zmile.hr/",
        },
        about: {
          "@type": "Organization",
          name: "Čajanka",
          url: "https://cajanka.hr/",
          parentOrganization: {
            "@type": "Organization",
            name: "Zmile d.o.o.",
          },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: copy.breadcrumb.home,
            item: props.locale === "hr" ? "https://zmile.hr/" : "https://zmile.hr/en",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: copy.breadcrumb.work,
            item: props.locale === "hr" ? props.alternate_urls.hr.replace("/cajanka", "") : props.alternate_urls.en.replace("/cajanka", ""),
          },
          {
            "@type": "ListItem",
            position: 3,
            name: copy.breadcrumb.current,
            item: props.canonical_url,
          },
        ],
      },
    ],
  }

  return (
    <>
      <MarketingMetadata
        alternateUrls={props.alternate_urls}
        canonicalUrl={props.canonical_url}
        description={props.description}
        locale={props.locale}
        noindex={props.noindex}
        socialImageUrl={props.social_image_url}
        structuredData={structuredData}
        title={props.title}
      />
      <MarketingLayout
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
        consultationHref={props.consultation_href}
      >
        <nav className="case-breadcrumbs" aria-label={props.locale === "hr" ? "Putanja stranice" : "Breadcrumb"}>
          <Link className="type-marketing-caption type-marketing-muted" href={props.home_href}>
            {copy.breadcrumb.home}
          </Link>
          <span className="type-marketing-caption type-marketing-muted" aria-hidden="true">/</span>
          <Link className="type-marketing-caption type-marketing-muted" href={props.work_href}>
            {copy.breadcrumb.work}
          </Link>
          <span className="type-marketing-caption type-marketing-muted" aria-hidden="true">/</span>
          <span className="type-marketing-caption type-marketing-muted" aria-current="page">
            {copy.breadcrumb.current}
          </span>
        </nav>

        <article className="case-study">
          <header className="case-hero">
            <div>
              <h1 className="type-marketing-hero-display">{copy.hero.heading}</h1>
            </div>
            <div className="case-hero-summary">
              <p className="type-marketing-lead">{copy.hero.summary}</p>
              <aside className="case-ownership-disclosure" id="ownership-disclosure">
                <span className="type-marketing-caption type-marketing-signal-display">
                  {copy.hero.ownershipLabel}
                </span>
                <p className="type-marketing-body-small type-marketing-ink">{copy.hero.ownership}</p>
              </aside>
              <a
                className="home-secondary-action type-marketing-action"
                href="https://cajanka.hr/"
                rel="noreferrer"
                target="_blank"
              >
                {copy.hero.liveLabel}
                <ArrowUpRight className="marketing-inline-icon" aria-hidden="true" />
              </a>
            </div>
          </header>

          <section className="case-context" aria-labelledby="case-context-heading">
            <div className="case-section-copy">
              <h2 className="type-marketing-section-display" id="case-context-heading">{copy.context.heading}</h2>
              <p className="type-marketing-prose">{copy.context.body}</p>
            </div>
            <dl className="case-facts">
              {copy.context.details.map((detail) => (
                <div key={detail.label}>
                  <dt className="type-marketing-caption type-marketing-muted">{detail.label}</dt>
                  <dd className="type-marketing-node type-marketing-ink">{detail.value}</dd>
                </div>
              ))}
            </dl>
            <figure className="case-storefront" data-project-evidence="real-storefront">
              <a href="https://cajanka.hr/" target="_blank" rel="noreferrer" aria-label={copy.hero.liveLabel}>
                <img src={webshopImage} alt={props.locale === "hr" ? "Stvarna Čajanka web-trgovina" : "The actual Čajanka webshop"} width="1440" height="1000" loading="lazy" />
              </a>
              <figcaption>{props.locale === "hr" ? "Čajanka.hr · stvarna web-trgovina, snimljena u rujnu 2026." : "Čajanka.hr · the actual webshop, captured in September 2026."}</figcaption>
            </figure>
          </section>

          <section className="case-system" aria-labelledby="case-system-heading">
            <header>
              <h2 className="type-marketing-section-display type-marketing-inverse" id="case-system-heading">{copy.system.heading}</h2>
              <p className="type-marketing-prose type-marketing-inverse-muted">{copy.system.body}</p>
            </header>
            <ol className="case-system-map" aria-label={copy.system.heading}>
              {copy.system.nodes.map((node, index) => (
                <li className="type-marketing-node type-marketing-inverse" key={node}>
                  <span className="type-marketing-index type-marketing-signal-display">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  {node}
                </li>
              ))}
            </ol>
          </section>

          <div className="case-chapters">
            {copy.chapters.map((chapter) => (
              <section className="case-chapter" aria-labelledby={`case-chapter-${chapter.number}`} key={chapter.number}>
                <header>
                  <span className="case-chapter-number type-marketing-index">{chapter.number}</span>
                  <div>
                    <p className="type-marketing-eyebrow type-marketing-accent-display">{chapter.eyebrow}</p>
                    <h2 className="type-marketing-section-display" id={`case-chapter-${chapter.number}`}>{chapter.heading}</h2>
                    <p className="type-marketing-prose">{chapter.body}</p>
                  </div>
                </header>
                <ul className="case-chapter-points">
                  {chapter.points.map((point) => (
                    <li className="type-marketing-node type-marketing-ink" key={point}>
                      <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
                      {point}
                    </li>
                  ))}
                </ul>
                <div className="case-chapter-media">
                  <OperationsMock copy={copy.mock} kind={chapter.mockKind} />
                  {chapter.mockKind === "stock" && <OperationsMock copy={copy.mock} kind="supplier" />}
                </div>
              </section>
            ))}
          </div>

          <section className="case-supporting" aria-labelledby="case-supporting-heading">
            <header>
              <h2 className="type-marketing-section-display" id="case-supporting-heading">{copy.supporting.heading}</h2>
              <p className="type-marketing-prose">{copy.supporting.body}</p>
            </header>
            <ol>
              {copy.supporting.items.map((item, index) => (
                <li className="type-marketing-node type-marketing-ink" key={item}>
                  <span className="type-marketing-index">{String(index + 1).padStart(2, "0")}</span>
                  {item}
                </li>
              ))}
            </ol>
          </section>

          <section className="case-outcomes" aria-labelledby="case-outcomes-heading" aria-describedby="ownership-disclosure">
            <header>
              <h2 className="type-marketing-section-display type-marketing-inverse" id="case-outcomes-heading">{copy.outcomes.heading}</h2>
              <p className="type-marketing-body-small type-marketing-inverse-muted">{copy.outcomes.disclosure}</p>
            </header>
            <ol>
              {copy.outcomes.items.map((outcome, index) => (
                <li key={outcome.value}>
                  <span className="type-marketing-index type-marketing-signal-display">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <strong className="type-marketing-metric type-marketing-inverse">{outcome.value}</strong>
                  <p className="type-marketing-body-small type-marketing-inverse">{outcome.label}</p>
                  <small className="type-marketing-microcopy type-marketing-inverse-muted">{outcome.calculation}</small>
                </li>
              ))}
            </ol>
          </section>

          <section className="case-conversion">
            <div>
              <h2 className="type-marketing-section-display">{copy.conversion.heading}</h2>
              <p className="type-marketing-prose">{copy.conversion.body}</p>
            </div>
            <div className="case-conversion-actions">
              <Link className="home-secondary-action type-marketing-action" href={props.services_href}>
                {copy.conversion.services}
              </Link>
              <Button asChild variant="marketing" size="marketingLg">
                <Link href={props.consultation_href} data-analytics-id="cajanka-consultation">
                  {copy.conversion.review}
                  <ArrowRight className="marketing-inline-icon" aria-hidden="true" />
                </Link>
              </Button>
              <Link className="home-secondary-action type-marketing-action" href={props.work_href}>
                {copy.conversion.conversation}
              </Link>
            </div>
          </section>
        </article>
      </MarketingLayout>
    </>
  )
}
